import { useCallback, useEffect, useState } from 'react'
import { apiFetch } from '../../../platform/lib/apiClient'

// Spec 150 (T022) — client typé des routes d'administration de la conservation des fichiers.
// Contrat : Docs/interne/pilotage/specs/historique/150-retention-fichiers/contracts/file-retention-api.md
// Aucune réponse ne porte de chemin, de nom de fichier ni d'identifiant de tenant.

export type OwnerState = 'subscriber' | 'other' | 'anonymized' | 'deleted' | 'orphan'
export type FileNature = 'snapshot' | 'groups' | 'report' | 'evaluation' | 'unknown'

export interface ExpiredFile {
  fileId: string
  nature: FileNature | string
  period: string | null
  observedAt: string
  expiresAt: string | null
  ageDays: number
  reason: 'duration' | 'anonymized' | 'deleted' | string | null
  deletable: boolean
}

export interface OwnerFiles {
  ownerId: string
  /** Compte du portail (lien vers la fiche, D-150-12) ; `null` pour un compte purgé. */
  accountId: string | null
  label: string
  state: OwnerState
  files: ExpiredFile[]
}

export interface PendingRequest {
  requestId: string
  ownerId: string | null
  status: string
  requestedAt: string
}

export interface FileRetentionOverview {
  asOf: string
  lastInventoryAt: string | null
  totals: { expiredFiles: number; expiredOwners: number; orphanFiles: number; oldestExpiredDays: number | null }
  owners: OwnerFiles[]
  orphans: { fileId: string; nature: string; producedAt: string | null }[]
  pendingRequests: PendingRequest[]
}

export interface DeletionRequestSummary {
  requestId: string
  ownerLabel: string
  status: string
  requestedAt: string
  requestedBy: string
  completedAt: string | null
  deleted: number
  failed: number
  skipped: number
}

export interface DeletionResult {
  ownerLabel: string
  nature: string
  period: string | null
  producedAt: string | null
  outcome: 'deleted' | 'failed' | 'skipped-not-expired' | 'skipped-absent' | string
  errorCode: string | null
}

export interface UseFileRetention {
  overview: FileRetentionOverview | null
  history: DeletionRequestSummary[]
  loading: boolean
  error: string | null
  /** `ownerId` null = toute la liste. Rien n'est supprimé immédiatement : l'importeur exécute la nuit suivante. */
  requestDeletion: (ownerId: string | null) => Promise<void>
  loadDetail: (requestId: string) => Promise<DeletionResult[]>
  reload: () => void
}

/** `accountId` (D-150-12) : les seuls fichiers de ce client, sans l'historique global des demandes. */
export function useFileRetention(accountId: string | null = null): UseFileRetention {
  const [overview, setOverview] = useState<FileRetentionOverview | null>(null)
  const [history, setHistory] = useState<DeletionRequestSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const overviewUrl = accountId ? `/api/file-retention?accountId=${encodeURIComponent(accountId)}` : '/api/file-retention'
    Promise.all([
      apiFetch<FileRetentionOverview>(overviewUrl),
      accountId ? Promise.resolve([] as DeletionRequestSummary[]) : apiFetch<DeletionRequestSummary[]>('/api/file-retention/deletions?limit=20'),
    ])
      .then(([o, h]) => {
        if (cancelled) return
        setOverview(o)
        setHistory(h)
      })
      .catch((err: unknown) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Erreur inconnue') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [tick, accountId])

  const reload = useCallback(() => setTick(t => t + 1), [])

  const requestDeletion = useCallback(async (ownerId: string | null) => {
    await apiFetch<{ requestId: string }>('/api/file-retention/deletions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ownerId }),
    })
    reload()
  }, [reload])

  const loadDetail = useCallback(async (requestId: string) => {
    const d = await apiFetch<{ results: DeletionResult[] }>(`/api/file-retention/deletions/${encodeURIComponent(requestId)}`)
    return d.results
  }, [])

  return { overview, history, loading, error, requestDeletion, loadDetail, reload }
}

// ─── Badge du menu d'administration (FR-020) ─────────────────────────────────────────────────────────

const BADGE_TTL_MS = 5 * 60_000
let badgeCache: { value: number; at: number } | null = null

/** Compteur des fichiers échus, sur l'onglet « Conservation » du hub Clients (reporté sur l'entrée « Clients »). */
export function useExpiredFilesBadge(enabled: boolean): Record<string, number> {
  const [count, setCount] = useState<number>(badgeCache?.value ?? 0)

  useEffect(() => {
    if (!enabled) return
    if (badgeCache && Date.now() - badgeCache.at < BADGE_TTL_MS) {
      setCount(badgeCache.value)
      return
    }
    let cancelled = false
    apiFetch<{ expiredFiles: number }>('/api/file-retention/count')
      .then(r => {
        badgeCache = { value: r.expiredFiles, at: Date.now() }
        if (!cancelled) setCount(r.expiredFiles)
      })
      .catch(() => { /* un compteur absent n'empêche rien : la page reste la source */ })
    return () => { cancelled = true }
  }, [enabled])

  return enabled && count > 0 ? { [FILE_RETENTION_ENTRY_ID]: count } : {}
}

/** Identifiant de l'onglet « Conservation » du hub Clients, clé de son compteur (D-150-12). */
export const FILE_RETENTION_ENTRY_ID = 'finops-conservation-hub'
